import PropTypes from "prop-types";

import { Button, Paper, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { formatMoney, formatNumber } from "@/features/shared";
import {
  DownstreamTransactionLinks,
  ProductVariantNameCell,
  UpstreamTransactionLink,
} from "@/features/ui";

export const ProductVariantsSection = ({
  referenceNumber,
  goodsReceiptProductVariants,
  warehouse,
}) => {
  const { hasPermission } = useAuth();

  if (!goodsReceiptProductVariants?.length) {
    return null;
  }

  // Custom export function that duplicates rows by quantity
  const handleExportExcel = () => {
    const expandedData = [];

    // Sort variants by name ascending
    const sortedVariants = [...goodsReceiptProductVariants].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Duplicate each row based on its quantity
    sortedVariants.forEach((variant) => {
      const quantity = variant.quantity;
      const retailPrice = variant.productVariant?.retailPrice;

      for (let i = 0; i < quantity; i++) {
        expandedData.push({
          "Produk Varian": variant.name,
          SKU: variant.sku,
          "Harga Retail": retailPrice != null ? formatMoney(retailPrice) : "",
        });
      }
    });

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(expandedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Produk Varian");

    // Download Excel file
    XLSX.writeFile(workbook, `${referenceNumber} - Produk Varian.xlsx`);
  };

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell
          name={name}
          sku={sku}
          linkToStock
          warehouseId={warehouse?.id}
          warehouseName={warehouse?.name}
        />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 1,
      renderCell: ({ row }) => formatNumber(row.quantity),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      flex: 1,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "purchaseOrder",
      headerName: "Pesanan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { purchaseOrder } }) => {
        // Determine which permission to check based on order type
        const hasReadPermission =
          purchaseOrder?.orderType === "LAB"
            ? hasPermission(PermissionEnum.READ_PURCHASE_ORDERS_LAB)
            : hasPermission(PermissionEnum.READ_PURCHASE_ORDERS_STANDARD);

        return (
          <UpstreamTransactionLink
            referenceNumber={purchaseOrder?.referenceNumber}
            quantity={purchaseOrder?.quantity}
            orderType={purchaseOrder?.orderType}
            clickable={hasReadPermission}
          />
        );
      },
    },
    {
      field: "purchaseInvoices",
      headerName: "Faktur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchaseInvoices}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_INVOICES)}
        />
      ),
    },
    {
      field: "purchasePayments",
      headerName: "Pembayaran",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchasePayments}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_PAYMENTS)}
        />
      ),
    },
    {
      field: "purchaseReturns",
      headerName: "Retur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchaseReturns}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_RETURNS)}
        />
      ),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 1.5,
      valueGetter: (_, row) => row.note || "-",
    },
  ];

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="subtitle1">Penerimaan Produk Varian</Typography>
          <Button variant="outlined" size="small" onClick={handleExportExcel}>
            Export Excel
          </Button>
        </Stack>
        <DataGrid
          getRowHeight={() => "auto"}
          columns={columns}
          rows={goodsReceiptProductVariants}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
        />
      </Stack>
    </Paper>
  );
};

ProductVariantsSection.propTypes = {
  referenceNumber: PropTypes.string.isRequired,
  goodsReceiptProductVariants: PropTypes.array.isRequired,
  warehouse: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};
